import { DocsCallout, DocsContent, DocsLayout, H2, H3 } from '@/components/docs'

export default function DocsSchemaConventions() {
  return (
    <DocsLayout>
      <DocsContent
        title="Schema Conventions"
        description="The shared vocabulary that schema authors and XENDREL both follow. Pick property names and types according to these conventions and the platform handles temporal/timeline integration and map rendering automatically — no per-schema configuration."
        previousPage={{ title: 'Timeline View', href: '/docs/timeline-view' }}
        nextPage={{ title: 'Audit Schema', href: '/docs/audit-schema' }}
      >
        <H2 id="why">Why conventions, not configuration</H2>
        <p className="text-foreground mb-4 leading-relaxed">
          A schema describes what data exists in your case. XENDREL has to know which properties
          carry dates, which entities have a position on a map, and which entities animate as moving
          tracks. The platform learns all of that from{' '}
          <strong>universal naming and typing conventions</strong> — written down once, followed
          everywhere. Authors don't fill in metadata blocks; they choose property names and types
          according to the table below and the rest happens automatically.
        </p>

        <DocsCallout variant="tip" title="One toggle, three render modes">
          The only behavioral switch a schema author has to flip is{' '}
          <code className="text-cyan-400">trackable: true</code> on entities that should animate as
          moving tracks (Vehicle, MobileDevice, Ship, …). Everything else — static pins,
          time-anchored events, timeline points/intervals — is derived from properties alone.
        </DocsCallout>

        <H2 id="temporal">Temporal properties</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          A property carries a temporal value when its declared type is one of:
        </p>

        <ul className="space-y-1 my-4 text-foreground">
          <InlineBullet>
            <code className="text-cyan-400">DATE</code> — a calendar day, no time component
          </InlineBullet>
          <InlineBullet>
            <code className="text-cyan-400">DATETIME</code> — a timestamp without timezone
          </InlineBullet>
          <InlineBullet>
            <code className="text-cyan-400">ZONED_DATETIME</code> — a timestamp with explicit
            timezone
          </InlineBullet>
        </ul>

        <p className="text-foreground mb-4 leading-relaxed">
          A property typed <code className="text-cyan-400">STRING</code> is <strong>never</strong>{' '}
          treated as temporal even if its name resembles a date (e.g.{' '}
          <code className="text-cyan-400">signed_date_str</code>). Always pick the typed temporal
          kind so the platform recognises it.
        </p>

        <H3 id="interval-pairs">Interval pairs</H3>

        <p className="text-foreground mb-4 leading-relaxed">
          Two temporal properties on the same entity or relation type are paired into a single
          interval aspect when their names match one of the following patterns. More specific
          patterns take precedence:
        </p>

        <div className="my-4 rounded-md border border-neutral-800 overflow-x-auto md:overflow-hidden">
          <table className="w-full min-w-lg wrap-normal text-sm md:min-w-0">
            <thead className="bg-neutral-900">
              <tr>
                <th className="px-3 py-2 text-left text-foreground font-semibold">
                  Start property
                </th>
                <th className="px-3 py-2 text-left text-foreground font-semibold">End property</th>
                <th className="px-3 py-2 text-left text-foreground font-semibold">Use for</th>
              </tr>
            </thead>
            <tbody className="text-foreground">
              <tr className="border-t border-neutral-800">
                <td className="px-3 py-2">
                  <code className="text-cyan-400">effective_from</code>
                </td>
                <td className="px-3 py-2">
                  <code className="text-cyan-400">effective_to</code>
                </td>
                <td className="px-3 py-2">Contract / role validity</td>
              </tr>
              <tr className="border-t border-neutral-800">
                <td className="px-3 py-2">
                  <code className="text-cyan-400">valid_from</code>
                </td>
                <td className="px-3 py-2">
                  <code className="text-cyan-400">valid_to</code>
                </td>
                <td className="px-3 py-2">Document / permission validity</td>
              </tr>
              <tr className="border-t border-neutral-800">
                <td className="px-3 py-2">
                  <code className="text-cyan-400">period_start</code>
                </td>
                <td className="px-3 py-2">
                  <code className="text-cyan-400">period_end</code>
                </td>
                <td className="px-3 py-2">Reporting periods</td>
              </tr>
              <tr className="border-t border-neutral-800">
                <td className="px-3 py-2">
                  <code className="text-cyan-400">opened_on</code>
                </td>
                <td className="px-3 py-2">
                  <code className="text-cyan-400">closed_on</code>
                </td>
                <td className="px-3 py-2">Account / case lifecycle</td>
              </tr>
              <tr className="border-t border-neutral-800">
                <td className="px-3 py-2">
                  <code className="text-cyan-400">incorporated_on</code>
                </td>
                <td className="px-3 py-2">
                  <code className="text-cyan-400">dissolved_on</code>
                </td>
                <td className="px-3 py-2">Company lifecycle</td>
              </tr>
              <tr className="border-t border-neutral-800">
                <td className="px-3 py-2">
                  <code className="text-cyan-400">from</code>
                </td>
                <td className="px-3 py-2">
                  <code className="text-cyan-400">to</code>
                </td>
                <td className="px-3 py-2">Roles, employments, residencies</td>
              </tr>
              <tr className="border-t border-neutral-800">
                <td className="px-3 py-2">
                  <code className="text-cyan-400">start</code>
                </td>
                <td className="px-3 py-2">
                  <code className="text-cyan-400">end</code>
                </td>
                <td className="px-3 py-2">Generic interval</td>
              </tr>
              <tr className="border-t border-neutral-800">
                <td className="px-3 py-2">
                  <code className="text-cyan-400">*_from</code>
                </td>
                <td className="px-3 py-2">
                  <code className="text-cyan-400">*_to</code>
                </td>
                <td className="px-3 py-2">
                  Suffix pattern (e.g. <code>period_from</code>/<code>period_to</code>)
                </td>
              </tr>
              <tr className="border-t border-neutral-800">
                <td className="px-3 py-2">
                  <code className="text-cyan-400">*_start</code>
                </td>
                <td className="px-3 py-2">
                  <code className="text-cyan-400">*_end</code>
                </td>
                <td className="px-3 py-2">Suffix pattern</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-foreground mb-4 leading-relaxed">
          Properties that don't match a pair pattern are emitted as <strong>point</strong> aspects
          on the timeline. The display label defaults to the title-cased property name (e.g.{' '}
          <code className="text-cyan-400">posted_at</code> → <em>Posted At</em>); to override, use
          the <code className="text-cyan-400">display_label</code> field on the entity type.
        </p>

        <H2 id="geolocation">Geolocation</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          An entity has <strong>inline geolocation</strong> when it declares a latitude and
          longitude pair, both numeric (<code className="text-cyan-400">FLOAT</code>,{' '}
          <code className="text-cyan-400">DECIMAL</code>,{' '}
          <code className="text-cyan-400">NUMBER</code>):
        </p>

        <ul className="space-y-1 my-4 text-foreground">
          <InlineBullet>
            <code className="text-cyan-400">lat</code> + <code className="text-cyan-400">lon</code>{' '}
            (preferred — short and matches Cesium APIs)
          </InlineBullet>
          <InlineBullet>
            <code className="text-cyan-400">latitude</code> +{' '}
            <code className="text-cyan-400">longitude</code> (also accepted)
          </InlineBullet>
        </ul>

        <H3 id="single-hop">Single-hop derivation</H3>

        <p className="text-foreground mb-4 leading-relaxed">
          An entity is <strong>geolocated</strong> when either of these holds:
        </p>

        <ol className="space-y-1 my-4 text-foreground list-decimal list-inside">
          <li>It has inline lat+lon properties.</li>
          <li>
            It has exactly one OUT relation whose target has inline lat+lon (e.g.{' '}
            <code className="text-cyan-400">SocialMediaPost -[OCCURRED_AT]-&gt; Location</code>).
          </li>
        </ol>

        <DocsCallout variant="warning" title="Two-hop walks are not supported">
          The convention is intentionally capped at one hop. If your only path to lat/lon goes
          through two intermediate edges, the entity is treated as <strong>not geolocated</strong>.
          The fix is to <strong>inline lat/lon on the intermediate node</strong> (see the v2 crime
          schema where BTSStation, FuelStation, and TrainCrossing carry their own lat/lon directly).
        </DocsCallout>

        <H2 id="trackable">Trackable: animated map tracks</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          Set <code className="text-cyan-400">trackable: true</code> on an entity type to declare
          that its instances move over time. The platform then derives a trajectory automatically:
        </p>

        <ol className="space-y-1 my-4 text-foreground list-decimal list-inside">
          <li>Find every OUT relation with at least one temporal property.</li>
          <li>Skip the relation if its target is not geolocated.</li>
          <li>
            Each surviving edge contributes a waypoint at <em>(timestamp, target's location)</em>.
          </li>
          <li>Order waypoints by timestamp — that's the trajectory.</li>
        </ol>

        <p className="text-foreground mb-4 leading-relaxed">
          You don't enumerate which edges contribute waypoints; the convention does it for you. A
          single <code className="text-cyan-400">trackable: true</code> on Vehicle picks up{' '}
          <code className="text-cyan-400">FUELED_AT</code>,{' '}
          <code className="text-cyan-400">PASSED_THROUGH</code>, and any future timestamped edge to
          a geolocated target — automatically.
        </p>

        <DocsCallout variant="warning" title="Mutually exclusive with inline lat/lon">
          A trackable entity does not carry its own coordinates — its position is derived from
          edges. If the entity declares its own <code>lat</code>+<code>lon</code> it is a static
          position, not a moving track. The schema editor disables the Trackable toggle whenever
          inline lat/lon is present, and the schema preview emits a{' '}
          <code>TRACKABLE_AND_INLINE_GEO</code> warning if both end up set (e.g. via JSON import).
          Pick one: inline coordinates for static infrastructure, or trackable for moving entities.
        </DocsCallout>

        <H2 id="render-modes">Map render modes</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The map (Cesium) chooses one of three render modes per entity, derived deterministically
          from the signals above:
        </p>

        <div className="my-4 rounded-md border border-neutral-800 overflow-x-auto md:overflow-hidden">
          <table className="w-full min-w-lg wrap-normal text-sm md:min-w-0">
            <thead className="bg-neutral-900">
              <tr>
                <th className="px-3 py-2 text-left text-foreground font-semibold">Mode</th>
                <th className="px-3 py-2 text-left text-foreground font-semibold">
                  Triggered when
                </th>
                <th className="px-3 py-2 text-left text-foreground font-semibold">Examples</th>
              </tr>
            </thead>
            <tbody className="text-foreground">
              <tr className="border-t border-neutral-800">
                <td className="px-3 py-2">
                  <strong>animated_track</strong>
                </td>
                <td className="px-3 py-2">
                  <code>trackable: true</code> + at least one waypoint source
                </td>
                <td className="px-3 py-2">Vehicle, MobileDevice</td>
              </tr>
              <tr className="border-t border-neutral-800">
                <td className="px-3 py-2">
                  <strong>time_anchored_pin</strong>
                </td>
                <td className="px-3 py-2">
                  Geolocated + at least one own temporal property; not trackable
                </td>
                <td className="px-3 py-2">SocialMediaPost, reified Transaction</td>
              </tr>
              <tr className="border-t border-neutral-800">
                <td className="px-3 py-2">
                  <strong>static_pin</strong>
                </td>
                <td className="px-3 py-2">Geolocated + no own temporal property; not trackable</td>
                <td className="px-3 py-2">BTSStation, FuelStation, Person (via RESIDES_AT)</td>
              </tr>
              <tr className="border-t border-neutral-800">
                <td className="px-3 py-2">
                  <em>(none)</em>
                </td>
                <td className="px-3 py-2">Not geolocated</td>
                <td className="px-3 py-2">PhoneNumber, Contract</td>
              </tr>
            </tbody>
          </table>
        </div>

        <DocsCallout
          variant="info"
          title="Why inbound temporal edges don't make a host time-anchored"
        >
          BTSStation is targeted by REGISTERED_AT (which carries{' '}
          <code className="text-cyan-400">occurred_at</code>), but the BTS itself doesn't appear and
          disappear with time — it stays put. Events that happen <em>at</em> a static entity are
          surfaced as separate timeline items. The host's pin only becomes time-anchored when its{' '}
          <strong>own</strong> properties carry a date.
        </DocsCallout>

        <H2 id="overrides">Overrides</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The conventions get you 95% of the way; the rest is per-type overrides on the entity or
          relation type. All four are optional and have safe defaults.
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <code className="text-cyan-400">trackable: true</code> (entity only) — opt into
              animated track mode.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <code className="text-cyan-400">timeline_exclude: ["dissolved_on"]</code> — list of
              property names to omit from the timeline (e.g. metadata-only dates that shouldn't
              appear as events).
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <code className="text-cyan-400">display_label</code>{' '}
              <code className="text-cyan-400">{'{ "signed_on": "Podpísaná" }'}</code> (entity only)
              — per-property label override; defaults to title-case of the property name.
            </span>
          </li>
        </ul>

        <H2 id="examples">Worked examples</H2>

        <H3 id="example-contract">Contract — multiple aspects, no map</H3>

        <pre className="my-4 p-4 rounded-md bg-neutral-900 border border-neutral-800 overflow-x-auto text-xs text-cyan-100">
          {`{
  "label": "Contract",
  "properties": [
    { "name": "contract_no",     "type": "STRING" },
    { "name": "signed_on",       "type": "DATE" },
    { "name": "effective_from",  "type": "DATE" },
    { "name": "effective_to",    "type": "DATE" },
    { "name": "next_due",        "type": "DATE" }
  ]
}`}
        </pre>

        <p className="text-foreground mb-4 leading-relaxed">Resolved automatically:</p>

        <ul className="space-y-1 my-4 text-foreground">
          <InlineBullet>
            Interval <code className="text-cyan-400">effective_from</code> →{' '}
            <code className="text-cyan-400">effective_to</code> — bar on the timeline labelled
            "Effective from".
          </InlineBullet>
          <InlineBullet>
            Point <code className="text-cyan-400">signed_on</code> — diamond labelled "Signed on".
          </InlineBullet>
          <InlineBullet>
            Point <code className="text-cyan-400">next_due</code> — diamond labelled "Next due".
          </InlineBullet>
          <InlineBullet>
            Map render: <em>none</em> (no geolocation).
          </InlineBullet>
        </ul>

        <H3 id="example-vehicle">Vehicle — animated track</H3>

        <pre className="my-4 p-4 rounded-md bg-neutral-900 border border-neutral-800 overflow-x-auto text-xs text-cyan-100">
          {`{
  "label": "Vehicle",
  "trackable": true,
  "properties": [ { "name": "spz", "type": "STRING" } ]
}

// Relevant relations & patterns:
//   Vehicle -[FUELED_AT { occurred_at: ZONED_DATETIME }]-> FuelStation
//   Vehicle -[PASSED_THROUGH { occurred_at: ZONED_DATETIME }]-> TrainCrossing
//   FuelStation, TrainCrossing — both with inline lat/lon properties`}
        </pre>

        <p className="text-foreground mb-4 leading-relaxed">
          Resolved automatically: <code className="text-cyan-400">animated_track</code> on the map.
          Waypoints come from both <code className="text-cyan-400">FUELED_AT</code> and{' '}
          <code className="text-cyan-400">PASSED_THROUGH</code>, sourced via the targets' inline
          lat/lon, ordered by <code className="text-cyan-400">occurred_at</code>.
        </p>

        <H3 id="example-bts">BTSStation — static pin</H3>

        <pre className="my-4 p-4 rounded-md bg-neutral-900 border border-neutral-800 overflow-x-auto text-xs text-cyan-100">
          {`{
  "label": "BTSStation",
  "properties": [
    { "name": "cell_id", "type": "STRING" },
    { "name": "lat",     "type": "FLOAT" },
    { "name": "lon",     "type": "FLOAT" }
  ]
}`}
        </pre>

        <p className="text-foreground mb-4 leading-relaxed">
          Resolved automatically: <code className="text-cyan-400">static_pin</code> on the map. The
          pin is always visible regardless of the playhead — events that happen at this BTS
          (REGISTERED_AT pings from MobileDevice) appear as separate items in the timeline, they do
          not change the BTS's render mode.
        </p>

        <H2 id="checking-your-schema">Checking your schema</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The Schema Design page (<code className="text-cyan-400">/admin/schema</code>) shows a live
          preview panel for every entity and relation type. As you edit properties the panel calls
          the schema preview API and tells you exactly what conventions resolved — which aspects,
          which geo mode, which render mode, plus any warnings (e.g. "this property looks like a
          date but is typed STRING").
        </p>

        <DocsCallout variant="tip" title="LLM-friendly contract">
          The conventions are deliberately stable and small: <code>DATE</code> /{' '}
          <code>DATETIME</code> for temporal types, <code>*_from</code> / <code>*_to</code> for
          interval pairs, <code>lat</code> + <code>lon</code> for inline geo, and{' '}
          <code>trackable: true</code> for animated tracks. An LLM extractor that knows this list
          can produce schemas that integrate with the timeline and map immediately, without any
          per-project fine-tuning.
        </DocsCallout>
      </DocsContent>
    </DocsLayout>
  )
}

/** Bullet whose text mixes inline code with prose. A flex row would lay every run out as its
 *  own column, which is unreadable on a phone, so below md the item flows as plain text. */
function InlineBullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="block md:flex items-start gap-2">
      <span className="text-amber-600 dark:text-amber-500 mt-1 mr-2 md:mr-0">•</span>
      {children}
    </li>
  )
}
